import "./documentByModelSearch.css";
import ScrollableComponent from "../ScrollableComponent";
import React, { useState, useEffect } from "react";
import { fetchWithAuth } from "../../utils/fetchWithAuth";
import {
  hasDbEditorUpdateAccessFromStorage,
} from "../../utils/authAccess";
import ViewListIcon from '@mui/icons-material/ViewList';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

export default function DocumentByModelSearch({
  data = [],
  setSelectedTab,
  setPartByModelData,
  setPartData,
  setImageData,
  setDocumentData,
}) {
  const [filteredData, setFilteredData] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [isRelatedModelsModalOpen, setIsRelatedModelsModalOpen] = useState(false);
  const [relatedModels, setRelatedModels] = useState([]);
  const [selectedModels, setSelectedModels] = useState([]);
  const [relatedModelsLoading, setRelatedModelsLoading] = useState(false);
  const [removeModelsLoading, setRemoveModelsLoading] = useState(false);
  const [relatedModelsError, setRelatedModelsError] = useState("");
  const [isAddRelatedModelModalOpen, setIsAddRelatedModelModalOpen] = useState(false);
  const [addRelatedModelNumber, setAddRelatedModelNumber] = useState("");
  const [addRelatedModelLoading, setAddRelatedModelLoading] = useState(false);
  const [addRelatedModelError, setAddRelatedModelError] = useState("");
  const [addRelatedProductTypeId, setAddRelatedProductTypeId] = useState("");
  const [addRelatedBrandId, setAddRelatedBrandId] = useState("");
  const [addRelatedBrands, setAddRelatedBrands] = useState([]);
  const [addRelatedBrandsLoading, setAddRelatedBrandsLoading] = useState(false);
  const [addRelatedProductTypes, setAddRelatedProductTypes] = useState([]);
  const [addRelatedProductTypesLoading, setAddRelatedProductTypesLoading] = useState(false);
  const [isModifyDocumentModalOpen, setIsModifyDocumentModalOpen] = useState(false);
  const [modifyTitle, setModifyTitle] = useState("");
  const [modifyDocInventoriedOn, setModifyDocInventoriedOn] = useState("");
  const [modifyDocIssuedOn, setModifyDocIssuedOn] = useState("");
  const [modifyDocType, setModifyDocType] = useState("");
  const [modifyDocumentLoading, setModifyDocumentLoading] = useState(false);
  const [modifyDocumentError, setModifyDocumentError] = useState("");
  const [originalDocumentData, setOriginalDocumentData] = useState(null);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [documentTypesLoading, setDocumentTypesLoading] = useState(false);
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] = useState(false);
  const [deleteDocumentLoading, setDeleteDocumentLoading] = useState(false);
  const [deleteDocumentError, setDeleteDocumentError] = useState("");
  const [modelHsin, setModelHsin] = useState(null);
  const [modelNumber, setModelNumber] = useState(null);

  const isAdmin = hasDbEditorUpdateAccessFromStorage();
  const username = localStorage.getItem("username") || "DBEditorWeb";

  useEffect(() => {
    setFilteredData(data);
    setSelectedDocuments([]);
    
    // Store hsin and modelNumber from the first document when data is available
    if (data && data.length > 0) {
      const firstDoc = data[0];
      const hsin = firstDoc.hsin || firstDoc.modelHsin || null;
      const modelNum = firstDoc.modelNumber || null;
      
      if (hsin) {
        setModelHsin(hsin);
      }
      if (modelNum) {
        setModelNumber(modelNum);
      }
    }
  }, [data]);

  const refreshDocumentData = async () => {
    if (!data || data.length === 0) {
      console.log("refreshDocumentData: No data available");
      return;
    }

    // Try to get hsin from stored value, or from data
    let hsin = modelHsin;
    
    if (!hsin && data && data.length > 0) {
      const modelInfo = data[0];
      hsin = modelInfo.hsin || modelInfo.modelHsin || null;
    }
    
    // If hsin is still not available, try to get it from stored modelNumber or from data
    if (!hsin) {
      const modelNum = modelNumber || (data && data.length > 0 ? data[0].modelNumber : null);
      if (modelNum) {
        try {
          console.log("refreshDocumentData: Looking up hsin for modelNumber:", modelNum);
          const modelData = await fetchWithAuth({
            method: "GET",
            endpoint: `/model/getModelByNumber`,
            params: { number: modelNum },
          });
          
          if (Array.isArray(modelData) && modelData.length > 0) {
            hsin = modelData[0].hsin;
            if (hsin) {
              setModelHsin(hsin);
              console.log("refreshDocumentData: Found hsin from model lookup:", hsin);
            }
          }
        } catch (err) {
          console.error("Error looking up model for hsin:", err);
        }
      }
    }
    
    if (!hsin) {
      console.log("refreshDocumentData: No hsin available. modelHsin:", modelHsin, "data[0]:", data?.[0]);
      return;
    }

    try {
      console.log("refreshDocumentData: Fetching documents with hsin:", hsin);
      const refreshedDocData = await fetchWithAuth({
        method: "GET",
        endpoint: `/model/getDocumentByModelNumber`,
        params: { hsin },
      });

      console.log("refreshDocumentData: Received data:", refreshedDocData);

      // Always update the data, even if it's an empty array (all documents inactive)
      if (Array.isArray(refreshedDocData)) {
        setFilteredData(refreshedDocData);
        setSelectedDocuments([]);
        
        if (setDocumentData) {
          setDocumentData(refreshedDocData);
        }
        console.log("refreshDocumentData: Data updated successfully. Count:", refreshedDocData.length);
      }
    } catch (err) {
      console.error("Error refreshing document data:", err);
    }
  };

  const closeErrorPopup = () => {
    setError("");
    setSelectedTab("DOCUMENTS");
  };

  const handleDocumentCheckboxChange = (row, index) => {
    const isSelected = selectedDocuments.some(selectedRow => selectedRow._uniqueIndex === index);

    if (isSelected) {
      setSelectedDocuments(selectedDocuments.filter(selectedRow => selectedRow._uniqueIndex !== index));
    } else {
      setSelectedDocuments([...selectedDocuments, { ...row, _uniqueIndex: index }]);
    }
  };

  const handleOpenRelatedModelsModal = async () => {
    if (selectedDocuments.length !== 1) return;
    
    const documentId = selectedDocuments[0].documentId;
    setIsRelatedModelsModalOpen(true);
    setRelatedModels([]);
    setSelectedModels([]);
    setRelatedModelsError("");
    setRelatedModelsLoading(true);

    try {
      const response = await fetchWithAuth({
        method: "GET",
        endpoint: `/document/getRelatedModelsByDocumentId`,
        params: { documentId },
      });
      setRelatedModels(Array.isArray(response) ? response : []);
    } catch (err) {
      console.error("Error fetching related models:", err);
      setRelatedModelsError("Error fetching related models.");
      setRelatedModels([]);
    } finally {
      setRelatedModelsLoading(false);
    }
  };

  const handleCloseRelatedModelsModal = async () => {
    console.log("handleCloseRelatedModelsModal: Closing modal and refreshing data");
    
    // Clear selected documents checkbox immediately
    setSelectedDocuments([]);
    
    setIsRelatedModelsModalOpen(false);
    setRelatedModels([]);
    setSelectedModels([]);
    setRelatedModelsError("");
    
    // Refresh document data when closing modal to reflect any changes (e.g., inactive documents)
    try {
      await refreshDocumentData();
    } catch (err) {
      console.error("Error refreshing data on modal close:", err);
    }
  };

  const handleModelCheckboxChange = (modelId, isChecked) => {
    if (isChecked) {
      setSelectedModels([...selectedModels, modelId]);
    } else {
      setSelectedModels(selectedModels.filter(id => id !== modelId));
    }
  };

  const handleRemoveRelatedModels = async () => {
    if (selectedModels.length === 0 || selectedDocuments.length !== 1) return;

    const documentId = selectedDocuments[0].documentId;
    const modelsToRemove = relatedModels.filter(model => 
      selectedModels.includes(model.id)
    );

    setRemoveModelsLoading(true);
    try {
      await fetchWithAuth({
        method: "PUT",
        endpoint: `/document/removeRelatedModels`,
        params: { documentId, username },
        data: modelsToRemove,
        headers: { "Content-Type": "application/json" },
      });

      // Refresh the related models list
      const response = await fetchWithAuth({
        method: "GET",
        endpoint: `/document/getRelatedModelsByDocumentId`,
        params: { documentId },
      });
      setRelatedModels(Array.isArray(response) ? response : []);
      setSelectedModels([]);
      
      // Refresh document data
      await refreshDocumentData();
    } catch (err) {
      console.error("Error removing related models:", err);
      setRelatedModelsError("Error removing related models.");
    } finally {
      setRemoveModelsLoading(false);
    }
  };

  const fetchProductTypes = async () => {
    setAddRelatedProductTypesLoading(true);
    try {
      const productTypesData = await fetchWithAuth({
        method: "GET",
        endpoint: "/taxonomy/allProductTypes",
      });
      setAddRelatedProductTypes(Array.isArray(productTypesData) ? productTypesData : []);
    } catch (err) {
      console.error("Error fetching product types:", err);
      setAddRelatedProductTypes([]);
    } finally {
      setAddRelatedProductTypesLoading(false);
    }
  };

  const fetchBrands = async () => {
    setAddRelatedBrandsLoading(true);
    try {
      const brandsData = await fetchWithAuth({
        method: "GET",
        endpoint: "/brands/getAllActiveBrands",
      });
      setAddRelatedBrands(Array.isArray(brandsData) ? brandsData : []);
    } catch (err) {
      console.error("Error fetching brands:", err);
      setAddRelatedBrands([]);
    } finally {
      setAddRelatedBrandsLoading(false);
    }
  };

  const handleOpenAddRelatedModelModal = async () => {
    if (selectedDocuments.length !== 1) return;
    setIsAddRelatedModelModalOpen(true);
    setAddRelatedModelNumber("");
    setAddRelatedProductTypeId("");
    setAddRelatedBrandId("");
    setAddRelatedModelError("");
    // Fetch product types and brands when modal opens
    await Promise.all([fetchProductTypes(), fetchBrands()]);
  };

  const handleCloseAddRelatedModelModal = () => {
    setIsAddRelatedModelModalOpen(false);
    setAddRelatedModelNumber("");
    setAddRelatedProductTypeId("");
    setAddRelatedBrandId("");
    setAddRelatedModelError("");
  };

  const handleAddRelatedModel = async () => {
    // Validate all three fields are filled
    if (selectedDocuments.length !== 1 || 
        !addRelatedProductTypeId.trim() || 
        !addRelatedBrandId.trim() || 
        !addRelatedModelNumber.trim()) {
      return;
    }

    const selectedDoc = selectedDocuments[0];
    const parentModelId = selectedDoc.modelId || data[0]?.modelId || selectedDoc.modelNumber || data[0]?.modelNumber || "";
    const parentDocumentId = selectedDoc.documentId;

    // Concatenate product type ID + brand ID + model number
    const concatenatedModelId = `${addRelatedProductTypeId.trim()}${addRelatedBrandId.trim()}${addRelatedModelNumber.trim()}`;

    setAddRelatedModelLoading(true);
    setAddRelatedModelError("");

    try {
      await fetchWithAuth({
        method: "PUT",
        endpoint: `/document/addRelatedModel`,
        params: { username },
        data: {
          parentModelId,
          parentDocumentId,
          modelId: concatenatedModelId,
        },
        headers: { "Content-Type": "application/json" },
      });

      // Close modal on success
      handleCloseAddRelatedModelModal();
      
      // Refresh document data
      await refreshDocumentData();
    } catch (err) {
      console.error("Error adding related model:", err);
      let errorMessage = "Error adding related model.";
      if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.data.error) {
          errorMessage = err.response.data.error;
        }
      }
      setAddRelatedModelError(errorMessage);
    } finally {
      setAddRelatedModelLoading(false);
    }
  };

  const fetchDocumentTypes = async () => {
    setDocumentTypesLoading(true);
    try {
      const response = await fetchWithAuth({
        method: "GET",
        endpoint: `/document/getAllDocumentTypes`,
      });
      setDocumentTypes(Array.isArray(response) ? response : []);
    } catch (err) {
      console.error("Error fetching document types:", err);
      setDocumentTypes([]);
    } finally {
      setDocumentTypesLoading(false);
    }
  };

  const handleOpenModifyDocumentModal = async () => {
    if (selectedDocuments.length !== 1) return;
    
    const selectedDoc = selectedDocuments[0];
    setOriginalDocumentData(selectedDoc);
    setModifyTitle(selectedDoc.docDescription || "");
    setModifyDocInventoriedOn(selectedDoc.docInventoriedOn || "");
    setModifyDocIssuedOn(selectedDoc.docIssuedOn || "");
    setModifyDocType(selectedDoc.docType || "");
    setModifyDocumentError("");
    setIsModifyDocumentModalOpen(true);
    
    // Fetch document types when modal opens
    await fetchDocumentTypes();
  };

  const handleCloseModifyDocumentModal = () => {
    setIsModifyDocumentModalOpen(false);
    setModifyTitle("");
    setModifyDocInventoriedOn("");
    setModifyDocIssuedOn("");
    setModifyDocType("");
    setModifyDocumentError("");
    setOriginalDocumentData(null);
    setDocumentTypes([]);
  };

  const hasChanges = () => {
    if (!originalDocumentData) return false;
    return (
      modifyTitle !== (originalDocumentData.docDescription || "") ||
      modifyDocInventoriedOn !== (originalDocumentData.docInventoriedOn || "") ||
      modifyDocIssuedOn !== (originalDocumentData.docIssuedOn || "") ||
      modifyDocType !== (originalDocumentData.docType || "")
    );
  };

  const handleModifyDocument = async () => {
    if (selectedDocuments.length !== 1 || !hasChanges()) return;

    const selectedDoc = selectedDocuments[0];
    setModifyDocumentLoading(true);
    setModifyDocumentError("");

    try {
      await fetchWithAuth({
        method: "PUT",
        endpoint: `/document/modifyDocument`,
        params: { username },
        data: {
          documentId: selectedDoc.documentId,
          title: modifyTitle,
          docIssuedOn: modifyDocIssuedOn,
          docInventoriedOn: modifyDocInventoriedOn,
          docTypeId: modifyDocType,
        },
        headers: { "Content-Type": "application/json" },
      });

      // Find the document type name from the selected ID
      const selectedDocType = documentTypes.find(dt => dt.id === modifyDocType);
      const docTypeName = selectedDocType ? selectedDocType.name : modifyDocType;

      const updatedData = filteredData.map((doc, index) => {
        if (doc._uniqueIndex === selectedDoc._uniqueIndex || 
            (doc.documentId === selectedDoc.documentId && doc.docType === originalDocumentData.docType)) {
          return {
            ...doc,
            docDescription: modifyTitle,
            docIssuedOn: modifyDocIssuedOn,
            docInventoriedOn: modifyDocInventoriedOn,
            docType: modifyDocType,
            docTypeName: docTypeName,
          };
        }
        return doc;
      });

      const updatedSelectedDocuments = selectedDocuments.map(doc => {
        if (doc._uniqueIndex === selectedDoc._uniqueIndex || 
            (doc.documentId === selectedDoc.documentId && doc.docType === originalDocumentData.docType)) {
          return {
            ...doc,
            docDescription: modifyTitle,
            docIssuedOn: modifyDocIssuedOn,
            docInventoriedOn: modifyDocInventoriedOn,
            docType: modifyDocType,
            docTypeName: docTypeName,
          };
        }
        return doc;
      });

      setFilteredData(updatedData);
      setSelectedDocuments(updatedSelectedDocuments);
      
      if (setDocumentData) {
        setDocumentData(updatedData);
      }

      handleCloseModifyDocumentModal();
      
      // Refresh document data from server
      await refreshDocumentData();
    } catch (err) {
      console.error("Error modifying document:", err);
      let errorMessage = "Error modifying document.";
      if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.data.error) {
          errorMessage = err.response.data.error;
        }
      }
      setModifyDocumentError(errorMessage);
    } finally {
      setModifyDocumentLoading(false);
    }
  };

  const handleOpenDeleteConfirmModal = () => {
    if (selectedDocuments.length === 0) return;
    setIsDeleteConfirmModalOpen(true);
    setDeleteDocumentError("");
  };

  const handleCloseDeleteConfirmModal = () => {
    setIsDeleteConfirmModalOpen(false);
    setDeleteDocumentError("");
  };

  const handleConfirmDeleteDocument = async () => {
    if (selectedDocuments.length === 0) return;

    const selectedDoc = selectedDocuments[0];
    setDeleteDocumentLoading(true);
    setDeleteDocumentError("");

    try {
      await fetchWithAuth({
        method: "PUT",
        endpoint: `/document/deleteDocumentById`,
        params: { 
          documentId: selectedDoc.documentId,
          username 
        },
      });

      // Remove the deleted document from the filtered data
      const updatedData = filteredData.filter((doc, index) => {
        return doc._uniqueIndex !== selectedDoc._uniqueIndex && 
               !(doc.documentId === selectedDoc.documentId && doc.docType === selectedDoc.docType);
      });

      setFilteredData(updatedData);
      setSelectedDocuments([]);
      
      if (setDocumentData) {
        setDocumentData(updatedData);
      }

      handleCloseDeleteConfirmModal();
      
      // Refresh document data from server
      await refreshDocumentData();
    } catch (err) {
      console.error("Error deleting document:", err);
      let errorMessage = "Error deleting document.";
      if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.data.error) {
          errorMessage = err.response.data.error;
        }
      }
      setDeleteDocumentError(errorMessage);
    } finally {
      setDeleteDocumentLoading(false);
    }
  };

  const handleClick = async (row) => {
    const model = row.modelNumber;
    setLoading(true);
    setError("");

    try {
      const partData = await fetchWithAuth({
        method: "GET",
        endpoint: `/model/getPartAndPagesByModel`,
        params: { modelNumber: model, documentId: row.documentId },
      });

      let enrichedPartData = partData;
      if (Array.isArray(partData) && partData.length > 0 && data && data.length > 0) {
        const modelInfo = data[0];
        enrichedPartData = [...partData];
        if (enrichedPartData[0]) {
          enrichedPartData[0] = {
            ...enrichedPartData[0],
            model: row.modelNumber || modelInfo.modelNumber || model || "",
            brndName: row.brandName || modelInfo.brandName || "",
            prdName: row.productName || modelInfo.productName || "",
            documentId: row.documentId, // Store documentId in the data for later use
          };
        }
      } else if (Array.isArray(partData) && partData.length > 0) {
        // Even if data is empty, still add documentId to the first item
        enrichedPartData = [...partData];
        if (enrichedPartData[0]) {
          enrichedPartData[0] = {
            ...enrichedPartData[0],
            documentId: row.documentId, // Store documentId in the data for later use
          };
        }
      }

      if (Array.isArray(enrichedPartData) && enrichedPartData.length > 0 && 
        enrichedPartData.some(item => item.partNumber != null || item.number != null)) {

        if (
          Array.isArray(enrichedPartData) &&
          enrichedPartData.length > 0 &&
          (row.docType === "PLDM" ||
            row.docType === "PLDMS" ||
            row.docType === "PLDT" ||
            row.docType === "PM")
        ) {

          setImageData(enrichedPartData);
          setSelectedTab("PARTS");
          setPartByModelData(enrichedPartData);
          setPartData([]);
        } else {
          setImageData(enrichedPartData);
          setSelectedTab("IMAGE");
          setPartByModelData([]);
          setPartData([]);
        }
      } else {
        setImageData(enrichedPartData);
        setSelectedTab("IMAGE");
        setPartByModelData([]);
        setPartData([]);
      }
    } catch (err) {
      console.error("Error fetching part data:", err);
      setError("Error fetching part data.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mainPageContent">
      {data && data.length !== 0 && (
        <table className="toptable" style={{ width: "100%", marginBottom: 0 }}>
          <thead>
            <tr>
              <td>
                <span>
                  <h5>Product Type : {data[0].productName} </h5>
                </span>{" "}
              </td>
              <td>
                <span>
                  <h5>Brand : {data[0].brandName}</h5>
                </span>
              </td>
              <td>
                <span>
                  <h5>Model :{data[0].modelNumber}</h5>
                </span>
              </td>
            </tr>
          </thead>
        </table>
      )}

      <ScrollableComponent height="400px">
        <table className="document" style={{ width: "100%" }}>
          <thead>
            <tr>
              <th>
                <input type="checkbox" disabled />
              </th>
              <th>Type</th>
              <th>Description</th>
              <th>Date Published</th>
              <th>Medium</th>
            </tr>
          </thead>
          <tbody>
            {filteredData &&
              filteredData.length !== 0 &&
              filteredData.map((row, index) => {
                const isSelected = selectedDocuments.some(selectedRow => selectedRow._uniqueIndex === index);
                
                return (
                <tr key={`doc-${index}-${row.documentId || ''}-${row.docType || ''}`}>
                  <td>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleDocumentCheckboxChange(row, index)}
                    />
                  </td>
                  <td
                    className="clickable-text"
                    onClick={() => handleClick(row)}
                  >
                    <b>{row.docTypeName}</b>
                  </td>
                  <td>{row.docDescription}</td>
                  <td>{row.docIssuedOn}</td>
                  <td>Image</td>
                </tr>
                );
              })}
          </tbody>
        </table>
      </ScrollableComponent>

      {isAdmin && (
        <div className="buttonPartGroup" style={{ marginTop: "16px" }}>
          <table className="buttonTable" style={{ width: "100%" }}>
            <thead>
              <tr>
                <td>
                  <button
                    className="btn btn-info"
                    onClick={handleOpenRelatedModelsModal}
                    disabled={selectedDocuments.length !== 1}
                  >
                    <ViewListIcon /> View/Remove Related Models
                  </button>
                </td>
                <td>
                  <button
                    className="btn btn-info"
                    onClick={handleOpenAddRelatedModelModal}
                    disabled={selectedDocuments.length !== 1}
                  >
                    <AddIcon /> Add Related Model
                  </button>
                </td>
                <td>
                  <button
                    className="btn btn-info"
                    onClick={handleOpenModifyDocumentModal}
                    disabled={selectedDocuments.length !== 1}
                  >
                    <EditIcon /> Modify Document
                  </button>
                </td>
                <td>
                  <button
                    className="btn btn-info"
                    onClick={handleOpenDeleteConfirmModal}
                    disabled={selectedDocuments.length === 0}
                  >
                    <DeleteIcon /> Delete Document
                  </button>
                </td>
              </tr>
            </thead>
          </table>
        </div>
      )}

      {loading && (
        <>
          <div className="blur-background"></div>
          <div className="loading"></div>
        </>
      )}

      {error && <div className="blur-overlay"></div>}

      {error && (
        <div className="popup">
          <div className="popup-message">{error}</div>
          <button className="popup-close" onClick={closeErrorPopup}>
            &times;
          </button>
        </div>
      )}

      {isRelatedModelsModalOpen && (
        <div className="modalOverlay">
          <div className="modalContent" style={{ minWidth: "600px", maxWidth: "800px" }}>
            <h3>Related Models</h3>
            
            {relatedModelsLoading && (
              <div style={{ textAlign: "center", padding: "20px" }}>
                <div className="button-spinner" style={{ margin: "0 auto" }}></div>
                <p>Loading related models...</p>
              </div>
            )}

            {!relatedModelsLoading && relatedModelsError && (
              <div style={{ textAlign: "center", padding: "20px", color: "red" }}>
                <p>{relatedModelsError}</p>
              </div>
            )}

            {!relatedModelsLoading && !relatedModelsError && (
              <>
                {relatedModels.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "20px" }}>
                    <p>No related models found.</p>
                  </div>
                ) : (
                  <div style={{ maxHeight: "500px", overflowY: "auto", marginBottom: "16px" }}>
                    <table className="part" style={{ width: "100%" }}>
                      <thead>
                        <tr>
                          <th>
                            <input
                              type="checkbox"
                              checked={relatedModels.length > 0 && selectedModels.length === relatedModels.length}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedModels(relatedModels.map(m => m.id));
                                } else {
                                  setSelectedModels([]);
                                }
                              }}
                            />
                          </th>
                          <th>Product Type</th>
                          <th>Brand</th>
                          <th>Model Number</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {relatedModels.map((model, index) => (
                          <tr key={index}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedModels.includes(model.id)}
                                onChange={(e) => handleModelCheckboxChange(model.id, e.target.checked)}
                              />
                            </td>
                            <td>{model.taxonomyName || "N/A"}</td>
                            <td>{model.brandName || "N/A"}</td>
                            <td>{model.formattedNumber || model.number || "N/A"}</td>
                            <td>{model.name || "N/A"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            <div className="modalActions">
              <button 
                onClick={handleRemoveRelatedModels}
                disabled={selectedModels.length === 0 || removeModelsLoading || relatedModelsLoading}
                style={{ 
                  backgroundColor: selectedModels.length === 0 ? '#ccc' : '#dc3545', 
                  color: 'white',
                  marginRight: '8px'
                }}
              >
                {removeModelsLoading ? (
                  <>
                    <div className="button-spinner" style={{ display: 'inline-block', marginRight: '8px' }}></div>
                    Removing...
                  </>
                ) : (
                  "Remove"
                )}
              </button>
              <button onClick={handleCloseRelatedModelsModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {isAddRelatedModelModalOpen && (
        <div className="modalOverlay" onClick={handleCloseAddRelatedModelModal}>
          <div className="modalContent relatedModelsModal" onClick={(e) => e.stopPropagation()} style={{ minWidth: "500px", maxWidth: "600px" }}>
            <h3>Add Related Model</h3>
            
            {addRelatedModelError && (
              <div className="errorText" style={{ marginBottom: "10px", color: "red" }}>
                {addRelatedModelError}
              </div>
            )}

            <div style={{ marginBottom: "16px" }}>
              <label htmlFor="productTypeId" style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                Product Type ID <span style={{ color: 'red' }}>*</span>:
              </label>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                {addRelatedProductTypesLoading ? (
                  <div style={{ padding: "8px 10px", fontSize: "14px", color: "#666", flex: 1 }}>
                    Loading product types...
                  </div>
                ) : (
                  <>
                    <select
                      id="productTypeId"
                      value={addRelatedProductTypeId}
                      onChange={(e) => setAddRelatedProductTypeId(e.target.value)}
                      disabled={addRelatedModelLoading}
                      style={{
                        flex: 1,
                        padding: "8px 10px",
                        fontSize: "14px",
                        border: "2px solid #007bff",
                        borderRadius: "4px",
                        boxSizing: "border-box",
                        backgroundColor: "white"
                      }}
                    >
                      <option value="">Select Product Type ID</option>
                      {addRelatedProductTypes.map((productType) => (
                        <option key={productType.id} value={productType.id}>
                          {[productType.name, productType.id].filter(Boolean).join(" - ")}
                        </option>
                      ))}
                    </select>
                  </>
                )}
              </div>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label htmlFor="brandId" style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                Brand <span style={{ color: 'red' }}>*</span>:
              </label>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                {addRelatedBrandsLoading ? (
                  <div style={{ padding: "8px 10px", fontSize: "14px", color: "#666", flex: 1 }}>
                    Loading brands...
                  </div>
                ) : (
                  <>
                    <select
                      id="brandId"
                      value={addRelatedBrandId}
                      onChange={(e) => setAddRelatedBrandId(e.target.value)}
                      disabled={addRelatedModelLoading}
                      style={{
                        flex: 1,
                        padding: "8px 10px",
                        fontSize: "14px",
                        border: "2px solid #007bff",
                        borderRadius: "4px",
                        boxSizing: "border-box",
                        backgroundColor: "white"
                      }}
                    >
                      <option value="">Select Brand ID</option>
                      {addRelatedBrands.map((brand) => (
                        <option key={brand.id} value={brand.id}>
                          {[brand.name, brand.id].filter(Boolean).join(" - ")}
                        </option>
                      ))}
                    </select>
                  </>
                )}
              </div>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label htmlFor="modelNumber" style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                Model Number <span style={{ color: 'red' }}>*</span>:
              </label>
              <input
                id="modelNumber"
                type="text"
                value={addRelatedModelNumber}
                onChange={(e) => setAddRelatedModelNumber(e.target.value)}
                placeholder="Enter Model Number"
                disabled={addRelatedModelLoading}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  fontSize: "14px",
                  border: "2px solid #007bff",
                  borderRadius: "4px",
                  boxSizing: "border-box"
                }}
              />
            </div>

            <div className="modalActions">
              <button
                onClick={handleAddRelatedModel}
                disabled={!addRelatedProductTypeId.trim() || !addRelatedBrandId.trim() || !addRelatedModelNumber.trim() || addRelatedModelLoading}
                style={{
                  backgroundColor: (!addRelatedProductTypeId.trim() || !addRelatedBrandId.trim() || !addRelatedModelNumber.trim()) ? '#ccc' : '#28a745',
                  color: 'white',
                  marginRight: '8px'
                }}
              >
                {addRelatedModelLoading ? (
                  <>
                    <div className="button-spinner"></div>
                    Saving...
                  </>
                ) : (
                  "Add"
                )}
              </button>
              <button onClick={handleCloseAddRelatedModelModal} disabled={addRelatedModelLoading}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {isModifyDocumentModalOpen && (
        <div className="modalOverlay" onClick={handleCloseModifyDocumentModal}>
          <div className="modalContent relatedModelsModal" onClick={(e) => e.stopPropagation()} style={{ minWidth: "500px", maxWidth: "600px" }}>
            <h3>Modify Document</h3>

            {modifyDocumentError && (
              <div className="errorText" style={{ marginBottom: "10px", color: "red" }}>
                {modifyDocumentError}
              </div>
            )}

            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                Document ID:
              </label>
              <div
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  fontSize: "14px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  boxSizing: "border-box",
                  backgroundColor: "#e9ecef",
                  color: "#6c757d",
                  cursor: "not-allowed"
                }}
              >
                {originalDocumentData?.documentId ?? "—"}
              </div>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label htmlFor="modifyTitle" style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                Title <span style={{ color: 'red' }}>*</span>:
              </label>
              <input
                id="modifyTitle"
                type="text"
                value={modifyTitle}
                onChange={(e) => setModifyTitle(e.target.value)}
                placeholder="Enter Title"
                disabled={modifyDocumentLoading}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  fontSize: "14px",
                  border: "2px solid #007bff",
                  borderRadius: "4px",
                  boxSizing: "border-box"
                }}
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label htmlFor="modifyDocInventoriedOn" style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                Doc Inventoried On:
              </label>
              <input
                id="modifyDocInventoriedOn"
                type="date"
                value={modifyDocInventoriedOn}
                onChange={(e) => setModifyDocInventoriedOn(e.target.value)}
                disabled={modifyDocumentLoading}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  fontSize: "14px",
                  border: "2px solid #007bff",
                  borderRadius: "4px",
                  boxSizing: "border-box"
                }}
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label htmlFor="modifyDocIssuedOn" style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                Doc Issued On:
              </label>
              <input
                id="modifyDocIssuedOn"
                type="date"
                value={modifyDocIssuedOn}
                onChange={(e) => setModifyDocIssuedOn(e.target.value)}
                disabled={modifyDocumentLoading}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  fontSize: "14px",
                  border: "2px solid #007bff",
                  borderRadius: "4px",
                  boxSizing: "border-box"
                }}
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label htmlFor="modifyDocType" style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                Document Type <span style={{ color: 'red' }}>*</span>:
              </label>
              {documentTypesLoading ? (
                <div style={{ padding: "8px 10px", fontSize: "14px", color: "#666" }}>
                  Loading document types...
                </div>
              ) : (
                <select
                  id="modifyDocType"
                  value={modifyDocType}
                  onChange={(e) => setModifyDocType(e.target.value)}
                  disabled={modifyDocumentLoading}
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    fontSize: "14px",
                    border: "2px solid #007bff",
                    borderRadius: "4px",
                    boxSizing: "border-box",
                    backgroundColor: "white"
                  }}
                >
                  <option value="">Select Document Type</option>
                  {documentTypes.map((docType) => (
                    <option key={docType.id} value={docType.id}>
                      {docType.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="modalActions">
              <button
                onClick={handleModifyDocument}
                disabled={!hasChanges() || modifyDocumentLoading || !modifyTitle.trim() || !modifyDocType.trim()}
                style={{
                  backgroundColor: !hasChanges() || !modifyTitle.trim() || !modifyDocType.trim() ? '#ccc' : '#28a745',
                  color: 'white',
                  marginRight: '8px'
                }}
              >
                {modifyDocumentLoading ? (
                  <>
                    <div className="button-spinner" style={{ display: 'inline-block', marginRight: '8px' }}></div>
                    Modifying...
                  </>
                ) : (
                  "Modify"
                )}
              </button>
              <button onClick={handleCloseModifyDocumentModal} disabled={modifyDocumentLoading}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {isDeleteConfirmModalOpen && (
        <div className="modalOverlay">
          <div className="modalContent" style={{ minWidth: "500px", maxWidth: "600px" }}>
            <h3>Confirm Delete</h3>
            {deleteDocumentError && (
              <div className="errorText" style={{ marginBottom: "10px", color: "red" }}>
                {deleteDocumentError}
              </div>
            )}
            <p>
              Are you sure you want to delete this document?
            </p>
            {selectedDocuments.length > 0 && (
              <div style={{ marginBottom: '16px', maxHeight: '200px', overflowY: 'auto' }}>
                <strong>Document to be deleted:</strong>
                <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                  <li style={{ marginBottom: '4px' }}>
                    {selectedDocuments[0].docTypeName || selectedDocuments[0].docType || 'N/A'} - {selectedDocuments[0].docDescription || 'N/A'}
                  </li>
                </ul>
              </div>
            )}
            <p style={{ color: '#dc3545', fontWeight: 'bold' }}>
              This action cannot be undone.
            </p>
            <div className="modalActions">
              <button 
                onClick={handleConfirmDeleteDocument} 
                disabled={deleteDocumentLoading} 
                style={{ backgroundColor: '#dc3545', color: 'white', marginRight: '8px' }}
              >
                {deleteDocumentLoading ? (
                  <>
                    <div className="button-spinner" style={{ display: 'inline-block', marginRight: '8px' }}></div>
                    Deleting...
                  </>
                ) : (
                  "Delete"
                )}
              </button>
              <button onClick={handleCloseDeleteConfirmModal} disabled={deleteDocumentLoading}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
